<template>
  <div v-if="hasImageSet" class="filmstrip-container">
    <!-- Header -->
    <div class="filmstrip-header">
      <h6 class="filmstrip-title">Image Set</h6>

      <!-- Window Type Dropdown (only show if multiple window types exist) -->
      <div v-if="windowTypes.length > 1" class="window-filter">
        <select
          v-model="selectedWindowType"
          class="form-select form-select-sm"
        >
          <option value="All">All Windows</option>
          <option v-for="windowType in windowTypes" :key="windowType" :value="windowType">
            {{ windowType }}
          </option>
        </select>
      </div>
    </div>

    <!-- Scrollable Thumbnails -->
    <div class="filmstrip-scroll" ref="scrollContainer">
      <div
        v-for="(image, index) in filteredImages"
        :key="image._id"
        class="filmstrip-thumbnail"
        :class="{
          'selected': isSelected(image),
          'has-pathology': hasPathology(image._id)
        }"
        @click="handleThumbnailClick(image)"
        :ref="el => { if (isSelected(image)) selectedThumbnailRef = el }"
      >
        <!-- Thumbnail Image -->
        <div
          class="thumbnail-image"
          :style="{ backgroundImage: `url(${getThumbnailUrl(image._id)})` }"
        >
          <!-- Loading Spinner -->
          <div v-if="loadingImageIds.has(image._id)" class="thumbnail-loading">
            <div class="spinner-border spinner-border-sm text-warning" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
          </div>

          <!-- Selection Checkbox -->
          <input
            type="checkbox"
            class="thumbnail-checkbox"
            :checked="isSelected(image)"
            @click.stop
            readonly
          />
        </div>

        <!-- Image Identifier Badge -->
        <div class="thumbnail-badge">
          <span class="badge badge-secondary badge-pill">
            {{ getImageIdentifier(image, index) }}
          </span>
        </div>
      </div>
    </div>

    <!-- Navigation Hint -->
    <div class="filmstrip-hint">
      <small>Use ← → arrow keys to navigate</small>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import { useStore } from 'vuex'
import { apiRoute } from '@/helpers'

const store = useStore()
const scrollContainer = ref(null)
const selectedThumbnailRef = ref(null)
const selectedWindowType = ref('All')
const loadingImageIds = ref(new Set())

// Computed properties
const imageSet = computed(() => store.state.labelerImageQueue.imageSet)
const currentImageId = computed(() => store.state.labelerImageQueue.currentImage?._id)
const hasImageSet = computed(() => imageSet.value.length > 1)
const annotations = computed(() => store.state.labelerAnnotation.annotations)
const imageSetAnnotations = computed(() => store.state.labelerImageQueue.imageSetAnnotations)

// Extract unique window types from image set
const windowTypes = computed(() => {
  const types = new Set()
  imageSet.value.forEach(img => {
    if (img.image_set?.window) {
      types.add(img.image_set.window)
    }
  })
  return Array.from(types).sort()
})

// Filter images by selected window type
const filteredImages = computed(() => {
  if (selectedWindowType.value === 'All') {
    return imageSet.value
  }
  return imageSet.value.filter(img => img.image_set?.window === selectedWindowType.value)
})

// Helper functions
const getThumbnailUrl = (imageId) => {
  return `${apiRoute()}/image/${imageId}?thumb=yes`
}

const getImageIdentifier = (image, index) => {
  const identifier = image.image_set?.identifier || image.name || `Image ${index + 1}`
  const window = image.image_set?.window || index + 1
  return `${identifier} - ${window}`
}

const isSelected = (image) => {
  return image._id === currentImageId.value
}

const hasPathology = (imageId) => {
  // Check current annotations if this is the current image
  if (imageId === currentImageId.value) {
    return annotations.value.length > 0
  }

  // Check saved annotations in imageSetAnnotations
  const savedData = imageSetAnnotations.value[imageId]
  return savedData && savedData.annotations && savedData.annotations.length > 0
}

const handleThumbnailClick = async (image) => {
  if (image._id === currentImageId.value) return

  // Set loading state
  loadingImageIds.value.add(image._id)

  try {
    // Save current image's annotations before switching
    await store.dispatch('labelerImageQueue/saveCurrentImageSetAnnotations')

    // Find the actual index in the full imageSet (not filtered)
    const actualIndex = imageSet.value.findIndex(img => img._id === image._id)

    // Switch to selected image using the actual index in the full set
    await store.dispatch('labelerImageQueue/selectImageFromSet', actualIndex)

    // Load saved annotations for this image (if any)
    await store.dispatch('labelerImageQueue/loadImageSetAnnotations', image._id)

    // Scroll thumbnail into view
    scrollToSelected()
  } finally {
    // Clear loading state after 1 second
    setTimeout(() => {
      loadingImageIds.value.delete(image._id)
    }, 1000)
  }
}

const scrollToSelected = () => {
  if (selectedThumbnailRef.value && scrollContainer.value) {
    selectedThumbnailRef.value.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center'
    })
  }
}

// Keyboard navigation
const handleKeyNavigation = async (event) => {
  if (!hasImageSet.value) return

  // Only handle arrow keys
  if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return

  // Don't interfere if user is typing in an input
  if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA') return

  event.preventDefault()

  // Navigate through filtered images, not the full set
  const currentFilteredIndex = filteredImages.value.findIndex(img => img._id === currentImageId.value)

  if (currentFilteredIndex === -1) return

  let newFilteredIndex = currentFilteredIndex

  if (event.key === 'ArrowRight') {
    // Move to next filtered image (wrap to first)
    newFilteredIndex = (currentFilteredIndex + 1) % filteredImages.value.length
  } else if (event.key === 'ArrowLeft') {
    // Move to previous filtered image (wrap to last)
    newFilteredIndex = currentFilteredIndex === 0 ? filteredImages.value.length - 1 : currentFilteredIndex - 1
  }

  if (newFilteredIndex !== currentFilteredIndex) {
    const newImage = filteredImages.value[newFilteredIndex]
    await handleThumbnailClick(newImage)
  }
}

// Watch for current image changes (auto-scroll)
watch(currentImageId, () => {
  setTimeout(scrollToSelected, 100)
})

// Watch for window filter changes - auto-select first image in filtered set
watch(selectedWindowType, async (newWindowType, oldWindowType) => {
  // Only trigger if window type actually changed and we have filtered images
  if (newWindowType !== oldWindowType && filteredImages.value.length > 0) {
    const firstFilteredImage = filteredImages.value[0]

    // Only switch if the current image is not in the filtered list
    const currentImageInFilteredList = filteredImages.value.some(img => img._id === currentImageId.value)

    if (!currentImageInFilteredList && firstFilteredImage) {
      // Save current annotations before switching
      await store.dispatch('labelerImageQueue/saveCurrentImageSetAnnotations')

      // Find actual index in full set
      const actualIndex = imageSet.value.findIndex(img => img._id === firstFilteredImage._id)

      // Switch to first filtered image
      await store.dispatch('labelerImageQueue/selectImageFromSet', actualIndex)

      // Load saved annotations for this image (if any)
      await store.dispatch('labelerImageQueue/loadImageSetAnnotations', firstFilteredImage._id)

      // Scroll into view
      setTimeout(scrollToSelected, 100)
    }
  }
})

// Lifecycle hooks
onMounted(() => {
  window.addEventListener('keydown', handleKeyNavigation)
  scrollToSelected()
})

onBeforeUnmount(() => {
  window.removeEventListener('keydown', handleKeyNavigation)
})
</script>

<style scoped>
.filmstrip-container {
  background-color: var(--color-bg-secondary);
  border: var(--border-width-card) solid var(--color-border-secondary);
  border-radius: var(--border-radius-small);
  padding: 0.75rem;
  margin-bottom: 1rem;
}

.filmstrip-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;
}

.filmstrip-title {
  color: #ffffff;
  font-size: 0.95rem;
  font-weight: 700;
  margin: 0;
  letter-spacing: 0.03em;
}

.window-filter {
  flex-shrink: 0;
}

.window-filter .form-select {
  background-color: #15385b;
  color: #ffffff;
  border: 1px solid #3981c8;
  font-size: 0.8rem;
  padding: 0.25rem 0.5rem;
  width: auto;
  min-width: 120px;
}

.window-filter .form-select:focus {
  background-color: #15385b;
  color: #ffffff;
  border-color: #f0b93b;
  box-shadow: 0 0 0 0.2rem rgba(240, 185, 59, 0.25);
}

.filmstrip-scroll {
  display: flex;
  gap: 10px;
  overflow-x: auto;
  overflow-y: hidden;
  padding: 0.5rem 0;
  scroll-behavior: smooth;
  margin: 0.5rem 0;
}

.filmstrip-scroll::-webkit-scrollbar {
  height: 8px;
}

.filmstrip-scroll::-webkit-scrollbar-track {
  background: var(--color-bg-primary);
  border-radius: 10px;
}

.filmstrip-scroll::-webkit-scrollbar-thumb {
  background: #0473b4;
  border-radius: 10px;
}

.filmstrip-scroll::-webkit-scrollbar-thumb:hover {
  background: #3981c8;
}

.filmstrip-thumbnail {
  flex-shrink: 0;
  min-width: 120px;
  max-width: 120px;
  cursor: pointer;
  transition: transform 0.2s ease;
}

.filmstrip-thumbnail:hover {
  transform: scale(1.05);
}

.thumbnail-image {
  position: relative;
  width: 100%;
  height: 100px;
  background-size: cover;
  background-position: center;
  background-color: #000;
  border: 2px solid #ccc;
  border-radius: 0.25rem;
  transition: border-color 0.2s ease;
}

.filmstrip-thumbnail.selected .thumbnail-image {
  border-color: #f0b93b;
  border-width: 3px;
}

.filmstrip-thumbnail.has-pathology .thumbnail-image {
  border-color: orange;
}

.filmstrip-thumbnail.selected.has-pathology .thumbnail-image {
  border-color: #f0b93b;
  box-shadow: 0 0 0 2px orange;
}

.thumbnail-loading {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: rgba(12, 32, 48, 0.8);
  border-radius: 50%;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.thumbnail-checkbox {
  position: absolute;
  top: 8px;
  right: 8px;
  cursor: pointer;
  width: 18px;
  height: 18px;
  accent-color: #f0b93b;
}

.thumbnail-badge {
  text-align: center;
  margin-top: 0.25rem;
}

.thumbnail-badge .badge {
  background-color: #f0b93b !important;
  color: #15385b !important;
  font-size: 0.7rem;
  font-weight: 600;
  padding: 0.25rem 0.5rem;
  word-wrap: break-word;
  white-space: normal;
  max-width: 100%;
  display: inline-block;
}

.filmstrip-hint {
  text-align: center;
  margin-top: 0.5rem;
}

.filmstrip-hint small {
  color: #878787;
  font-size: 0.75rem;
  font-style: italic;
}
</style>
